import { getPref, setPref } from '../../db/prefs.ts';
import type { SourceId } from '../../sources/types.ts';

export const RECENT_SEARCHES_KEY = 'search.recent';
export const MAX_RECENT_SEARCHES = 12;

export interface RecentSearch {
  id: string;
  q: string;
  scope: SourceId | 'all';
  url: string;
  createdAt: number;
}

export interface RecordRecentSearchInput {
  q: string;
  scope: SourceId | 'all';
  url: string;
  now?: number;
}

function normalizeQuery(q: string): string {
  return q.trim().replace(/\s+/g, ' ');
}

export function upsertRecentSearch(
  rows: RecentSearch[],
  input: RecordRecentSearchInput,
): RecentSearch[] {
  const q = normalizeQuery(input.q);
  if (!q) return rows;
  const createdAt = input.now ?? Date.now();
  const id = `${input.scope}:${q.toLowerCase()}`;
  const next: RecentSearch = {
    id,
    q,
    scope: input.scope,
    url: input.url,
    createdAt,
  };
  return [next, ...rows.filter((row) => row.id !== id)].slice(0, MAX_RECENT_SEARCHES);
}

export async function listRecentSearches(): Promise<RecentSearch[]> {
  const rows = await getPref<RecentSearch[]>(RECENT_SEARCHES_KEY);
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row): row is RecentSearch => {
      return (
        typeof row === 'object' &&
        row !== null &&
        typeof row.id === 'string' &&
        typeof row.q === 'string' &&
        typeof row.scope === 'string' &&
        typeof row.url === 'string' &&
        typeof row.createdAt === 'number'
      );
    })
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, MAX_RECENT_SEARCHES);
}

export async function recordRecentSearch(input: RecordRecentSearchInput): Promise<void> {
  const rows = await listRecentSearches();
  await setPref(RECENT_SEARCHES_KEY, upsertRecentSearch(rows, input));
}
